"use client";

import type { ReactNode } from "react";
import { ChevronDown, Search, SlidersHorizontal, X } from "lucide-react";

import { Popover } from "../ui";
import "./browse.css";

export { default as SortMenu } from "./SortMenu";

/**
 * THE v2 BROWSE KIT — the controls every catalogue route puts in its quick
 * bar, as small presentational pieces with no data of their own. Each one is
 * given its value and a callback; the route owns the state (in the URL, via
 * `useBrowseParams`). `/collection` is the first user; `/market` draws the
 * same controls by hand today and can move onto these as-is.
 */

export type IconOption<T extends string> = { value: T; label: string; icon?: ReactNode };

/** The search field: icon, input, and a clear button while there is text. */
export function SearchField({ id, value, onChange, placeholder, label, clearLabel }: {
  id: string; value: string; onChange: (value: string) => void; placeholder: string; label: string; clearLabel: string;
}) {
  return (
    <div className="v2-bx-search">
      <label htmlFor={id} className="v2-sr-only">{label}</label>
      <Search aria-hidden />
      <input id={id} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {value ? <button type="button" className="v2-bx-clear v2-focus" onClick={() => onChange("")} aria-label={clearLabel}><X aria-hidden /></button> : null}
    </div>
  );
}

export type FacetGroup = {
  id: string;
  label: string;
  allLabel: string;
  options: { value: string; label: string }[];
  value: string | null;
  onChange: (value: string | null) => void;
};

/** One "Filters" trigger that opens every facet as a group of chips. */
export function FacetPopover({ label, groups, activeCount, formatCount }: {
  label: string; groups: FacetGroup[]; activeCount: number; formatCount: (n: number) => string;
}) {
  return (
    <Popover
      align="end"
      side="auto"
      avoidViewportBottom={88}
      label={label}
      className="v2-bx-facets"
      trigger={({ open, toggle, id }) => (
        <button type="button" className="v2-bx-trigger v2-focus" data-open={open || undefined} onClick={toggle} aria-expanded={open} aria-controls={id}>
          <SlidersHorizontal aria-hidden /><span>{label}</span>{activeCount ? <b>{formatCount(activeCount)}</b> : null}<ChevronDown aria-hidden />
        </button>
      )}
    >
      {groups.map((g) => (
        <div key={g.id} className="v2-bx-facet">
          <span id={`v2-bx-${g.id}`}>{g.label}</span>
          <div role="group" aria-labelledby={`v2-bx-${g.id}`}>
            <button type="button" aria-pressed={!g.value} onClick={() => g.onChange(null)}>{g.allLabel}</button>
            {g.options.map((o) => (
              <button key={o.value} type="button" aria-pressed={g.value === o.value} onClick={() => g.onChange(g.value === o.value ? null : o.value)}>{o.label}</button>
            ))}
          </div>
        </div>
      ))}
    </Popover>
  );
}

/** Icon-only toggle buttons for mutually exclusive views (grid / table). */
export function ViewToggle<T extends string>({ value, options, onChange, label }: {
  value: T; options: IconOption<T>[]; onChange: (value: T) => void; label: string;
}) {
  return (
    <div className="v2-bx-views" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className="v2-focus" aria-pressed={value === o.value} onClick={() => onChange(o.value)} aria-label={o.label} title={o.label}>{o.icon}</button>
      ))}
    </div>
  );
}

/** Labelled toggle buttons for the route's tabs (top / trending / …). */
export function TabSwitch<T extends string>({ value, options, onChange, label }: {
  value: T; options: IconOption<T>[]; onChange: (value: T) => void; label: string;
}) {
  return (
    <div className="v2-bx-tabs" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" className="v2-focus" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>{o.icon}{o.label}</button>
      ))}
    </div>
  );
}

export type AppliedChip = { key: string; label: string; onRemove: () => void };

/** The filters in force, each removable, and one "clear all". */
export function AppliedFilters({ chips, label, clearLabel, onClear }: {
  chips: AppliedChip[]; label: string; clearLabel: string; onClear: () => void;
}) {
  if (chips.length === 0) return null;
  return (
    <div className="v2-bx-applied" aria-label={label}>
      {chips.map((c) => <button key={c.key} type="button" className="v2-focus" onClick={c.onRemove}>{c.label}<X aria-hidden /></button>)}
      <button type="button" className="v2-bx-reset v2-focus" onClick={onClear}>{clearLabel}</button>
    </div>
  );
}
